// Frank mirrors the picked ManufacturingLineList (plan) into the address bar so the selection survives a page
// refresh and the link can be shared. Each section owns one query parameter, manufacturing_line_<index> -- the
// SAME name and compact machine-id format the legacy Manufacturing Cost Calculator uses, so a link reads the
// same across both.
//
// Frank's plan key is the ManufacturingLineList#identifier: its manufacturing lines' identifiers joined by "|"
// (ManufacturingLine#identifier is "manufacturing_line:offset:<printId>:<dieId>" for an offset pair, or
// "manufacturing_line:flexo:<id>" for a combined flexo line). The address bar carries only the machine ids --
// everything after "manufacturing_line:<type>:" -- so it reads "manufacturing_line_0=10031-12008" instead of
// the percent-encoded identifier. The id COUNT recovers the type on the way back (2 ids -> offset pair, 1 id ->
// flexo), so the round trip reproduces the identifier exactly -- the same string the listing exposed and the
// solutions endpoint keys on.

const parameterName = (index) => `manufacturing_line_${index}`;

const MACHINE_ID_SEPARATOR = '-';
const LINE_SEPARATOR = '_';
const LINE_IDENTIFIER_SEPARATOR = '|';

// The machine ids inside a line identifier: everything after "manufacturing_line:<type>:".
const machineIdsOf = (lineIdentifier) => lineIdentifier.split(':').slice(2);

// Serialize one line identifier to its machine ids joined by "-" ("<printId>-<dieId>", or "<id>" for flexo).
const serializeLine = (lineIdentifier) => machineIdsOf(lineIdentifier).join(MACHINE_ID_SEPARATOR);

// Parse one token back to a line identifier, reconstructing the type from the machine id count (2 ids -> offset
// pair, 1 id -> flexo combined). Returns null for a malformed token (an empty or non-numeric id, or an id count
// other than 1 or 2) so a corrupt / stale-format param is rejected rather than restored as a bogus plan.
const parseLine = (token) => {
  const ids = token.split(MACHINE_ID_SEPARATOR);
  if (!ids.every((id) => id !== '' && !Number.isNaN(Number(id)))) { return null; }
  if (ids.length === 2) { return `manufacturing_line:offset:${ids[0]}:${ids[1]}`; }
  if (ids.length === 1) { return `manufacturing_line:flexo:${ids[0]}`; }
  return null;
};

// The URL value for a plan identifier: each line's machine ids, lines joined by "_". This round-trips exactly
// to the "|"-joined identifier the listing exposed, so the stored value and the plan key can never drift.
// Null / empty identifier yields an empty string.
export const manufacturingLineListParameterValue = (identifier) => (identifier || '')
  .split(LINE_IDENTIFIER_SEPARATOR)
  .filter(Boolean)
  .map(serializeLine)
  .join(LINE_SEPARATOR);

// The picked plan's identifier for this section (the "|"-joined line identifiers), or null when none is
// selected or the stored value is malformed. If ANY line fails to parse the whole param is treated as "no
// selection" (falls back to the listing) rather than restoring a partial / bogus plan.
export const readSelectedManufacturingLineList = (index) => {
  const value = new URLSearchParams(window.location.search).get(parameterName(index));
  if (!value) { return null; }
  const lines = value.split(LINE_SEPARATOR).map(parseLine);
  if (lines.some((line) => line === null)) { return null; }
  return lines.length > 0 ? lines.join(LINE_IDENTIFIER_SEPARATOR) : null;
};

// Keep the address bar in sync with this section's picked plan so a refresh / shared link reproduces it.
// replaceState (not pushState) is deliberate -- this is a mirror, not in-page navigation: it adds no history
// entries, so Back never lands mid-flow. A same-origin relative URL is used so only the query string changes.
export const writeSelectedManufacturingLineList = (index, identifier) => {
  const url = new URL(window.location.href);
  url.searchParams.set(parameterName(index), manufacturingLineListParameterValue(identifier));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

// Remove this section's plan from the address bar (used by "back to machines" / a cleared selection).
export const clearSelectedManufacturingLineList = (index) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(parameterName(index));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};
